import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  X,
  Building2,
  FileText,
  Trash2,
  Star,
  Upload,
  ChevronDown,
  Search,
  Mail,
  Phone,
} from 'lucide-react';

import { LoadingSpinner, PageLoadingState } from '../components/LoadingSpinner';
import { AppTopBar } from '../components/AppTopBar';
import { AppSidebar } from '../components/AppSidebar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AddressAutocompleteInput } from '../components/AddressAutocompleteInput';
import { CreateContactModal } from '../features/contacts/CreateContactModal';
import { OrganizationTypePicker } from '../features/contacts/OrganizationTypePicker';
import {
  Contact,
  Organization,
  OrganizationType,
  contactDisplayName,
  type Lead,
} from '../types';
import {
  contactsHubPath,
  contactDetailPath,
} from '../utils/contactRoutes';
import { leadDetailPath, projectsListPath } from '../utils/leadRoutes';
import { organizationAvatarClassName } from '../utils/entityAvatarClasses';
import { InvoiceDocumentsList } from '../features/contacts/InvoiceDocumentsList';
import { LeadProjectsSection } from '../features/contacts/LeadProjectsSection';
import { CreateLeadModal } from '../features/leads/components/CreateLeadModal';
import { CustomerPartySummary } from '../features/leads/components/CustomerPartySummary';
import { ComposeEmailModal } from '../features/inbox/components/ComposeEmailModal';
import { fetchDispatchAccount } from '../utils/mailDispatchApi';
import { formatIbanDisplay, isValidIban, normalizeIban } from '../utils/iban';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNavigateBack } from '../hooks/useNavigateBack';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, APP_INPUT_CLASS, FILTER_GROUP_LABEL_CLASS } from '../ui/controlStyles';
import { AppSelect } from '../components/AppSelect';
import { useDatevStatus } from '../features/datev/useDatevStatus';
import { useDatevCostCenters, type DatevCostCenter } from '../features/datev/useDatevCostCenters';

type LeadLike = {
  id: string;
  display_id?: string | null;
  name?: string;
  lead_title?: string | null;
  status?: string;
  category?: string;
  created_at?: string;
  contact_id?: string | null;
  site_address?: string | null;
  offer_net_total?: number | null;
  offer_gross_total?: number | null;
};

type MaterialAgg = {
  material_id: string | null;
  item_name: string;
  total_quantity: number;
  unit: string | null;
  total_value: number;
};

type InvoiceDoc = {
  id: string;
  lead_id: string | null;
  direction: 'outgoing' | 'incoming';
  document_type: string;
  invoice_number: string | null;
  filename: string;
  issue_date: string | null;
  due_date?: string | null;
  paid_at?: string | null;
  net_amount: number | null;
  gross_amount: number | null;
  status: string;
  lead_display_id: string | null;
  lead_title: string | null;
  created_at: string;
};

type OrgDocument = {
  id: string;
  organization_id: string;
  filename: string;
  mime_type: string | null;
  size: number;
  created_at: string;
};

const formatCurrency = (v: number | null | undefined): string =>
  v == null
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

const formatDateDe = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString('de-DE') : '—';

const formatPercent = (v: number | null | undefined): string =>
  v == null ? '—' : `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(Number(v))} %`;

function orgInitials(name: string): string {
  const t = name.trim();
  if (!t) return '?';
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return t.slice(0, 2).toUpperCase();
}

async function parseApiError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    /* ignore */
  }
  return fallback;
}

export const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canOpen, loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Projekte, Materialien und Rechnungen der Organisation kommen über Quer-Routen, die das jeweilige Modul brauchen.
  const projectsEnabled = modulesLoaded && isEnabled('projekte');
  const materialsEnabled = modulesLoaded && isEnabled('stammdaten');
  const invoicesEnabled = modulesLoaded && isEnabled('rechnungen');
  const goBack = useNavigateBack(contactsHubPath('organizations'));

  const [org, setOrg] = React.useState<Organization | null>(null);
  const [loadingOrg, setLoadingOrg] = React.useState(true);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [leads, setLeads] = React.useState<LeadLike[]>([]);
  const [materials, setMaterials] = React.useState<MaterialAgg[]>([]);
  const [invoices, setInvoices] = React.useState<InvoiceDoc[]>([]);
  const [documents, setDocuments] = React.useState<OrgDocument[]>([]);
  const [constructionDeductionsEnabled, setConstructionDeductionsEnabled] = React.useState(false);
  const [editingSection, setEditingSection] = React.useState<OrgEditSection | null>(null);
  const [saving, setSaving] = React.useState(false);
  /** Geänderte IBAN: erst nach ausdrücklicher Bestätigung speichern (Betrugsschutz). */
  const [ibanConfirm, setIbanConfirm] = React.useState<{ patch: Partial<Organization>; previous: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const datev = useDatevStatus();
  const datevCostCenters = useDatevCostCenters(datev.enabled);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [leadCreateOpen, setLeadCreateOpen] = React.useState(false);
  const [leadCreatePrefillContactId, setLeadCreatePrefillContactId] = React.useState<string | null>(null);
  const [emailCompose, setEmailCompose] = React.useState<{
    accountId: string | null;
    to: string;
    cc: string;
    subject: string;
    body: string;
    fromEmail?: string | null;
  } | null>(null);
  const [emailComposeResetKey, setEmailComposeResetKey] = React.useState(0);
  const [emailComposeBusy, setEmailComposeBusy] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const orgDocumentTitle = React.useMemo(() => {
    if (!org || !id || String(org.id) !== String(id)) return null;
    return org.name?.trim() || null;
  }, [org, id]);
  useDocumentTitle(orgDocumentTitle);

  const reload = React.useCallback((options?: { quiet?: boolean }): Promise<void> => {
    if (!id) return Promise.resolve();
    const quiet = options?.quiet === true;
    if (!quiet) setLoadingOrg(true);
    return Promise.all([
      fetch(`/api/organizations/${id}`).then((r) => (r.ok ? r.json() : null)),
      fetch(`/api/organizations/${id}/contacts`).then((r) => (r.ok ? r.json() : [])),
      projectsEnabled ? fetch(`/api/organizations/${id}/leads`).then((r) => (r.ok ? r.json() : [])) : Promise.resolve([]),
      materialsEnabled
        ? fetch(`/api/organizations/${id}/materials`).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),
      invoicesEnabled
        ? fetch(`/api/organizations/${id}/invoice-documents`).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),
      fetch(`/api/organizations/${id}/documents`).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/settings/company', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([o, cs, ls, ms, inv, docs, companySettings]) => {
        setOrg(o);
        setContacts(Array.isArray(cs) ? cs : []);
        setLeads(Array.isArray(ls) ? ls : []);
        setMaterials(Array.isArray(ms) ? ms : []);
        setInvoices(Array.isArray(inv) ? inv : []);
        setDocuments(Array.isArray(docs) ? docs : []);
        setConstructionDeductionsEnabled(companySettings?.construction_deductions_enabled === true);
      })
      .catch(() => setError('Organisation konnte nicht geladen werden.'))
      .finally(() => {
        if (!quiet) setLoadingOrg(false);
      });
  }, [id, projectsEnabled, materialsEnabled, invoicesEnabled]);

  React.useEffect(() => {
    if (!modulesLoaded) return;
    reload();
  }, [reload, modulesLoaded]);

  const showConstructionDeductions =
    constructionDeductionsEnabled ||
    Number(org?.site_levy_percent) > 0 ||
    Number(org?.retention_percent_partial) > 0 ||
    Number(org?.retention_percent_final) > 0;

  const lastProjectDateDe = React.useMemo(() => {
    if (leads.length === 0) return null;
    const times = leads
      .map((l) => (l.created_at ? new Date(l.created_at).getTime() : NaN))
      .filter((t) => Number.isFinite(t));
    if (times.length === 0) return null;
    return formatDateDe(new Date(Math.max(...times)).toISOString());
  }, [leads]);

  const orgRevenueTotals = React.useMemo(() => {
    let gross = 0;
    let net = 0;
    let nGross = 0;
    let nNet = 0;
    for (const l of leads) {
      const g = l.offer_gross_total;
      const n = l.offer_net_total;
      if (g != null && Number.isFinite(Number(g))) {
        gross += Number(g);
        nGross++;
      }
      if (n != null && Number.isFinite(Number(n))) {
        net += Number(n);
        nNet++;
      }
    }
    return {
      grossSum: nGross > 0 ? gross : null,
      netSum: nNet > 0 ? net : null,
    };
  }, [leads]);

  const primaryContact = React.useMemo(() => {
    if (org?.primary_contact_id) {
      return contacts.find((c) => String(c.id) === String(org.primary_contact_id)) ?? null;
    }
    return contacts.find((c) => c.is_primary) ?? null;
  }, [contacts, org?.primary_contact_id]);

  const openPrimaryContactEmailCompose = React.useCallback(async () => {
    if (!primaryContact?.email || emailComposeBusy) return;
    setEmailComposeBusy(true);
    try {
      const dispatch = await fetchDispatchAccount('general');
      if (!dispatch.account_id && !dispatch.can_choose) {
        throw new Error('Kein sendbares Postfach eingerichtet.');
      }
      const recipientName = contactDisplayName(primaryContact);
      setEmailCompose({
        accountId: dispatch.account_id,
        to: primaryContact.email.trim(),
        cc: '',
        subject: '',
        body: recipientName ? `Hallo ${recipientName},\n\n` : '',
        fromEmail: dispatch.email,
      });
      setEmailComposeResetKey((k) => k + 1);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setEmailComposeBusy(false);
    }
  }, [primaryContact, emailComposeBusy]);

  if (loadingOrg) {
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
        <AppSidebar active="contacts" />
        <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Organisation" />
          <PageLoadingState />
        </main>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
        <AppSidebar active="contacts" />
        <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Organisation" />
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-sm text-app-text-subtle">
            <p>{error ?? 'Organisation nicht gefunden.'}</p>
            <button onClick={() => navigate(contactsHubPath('organizations'))} className="btn-pipedrive-secondary">
              Zur Übersicht
            </button>
          </div>
        </main>
      </div>
    );
  }

  const executeDeleteOrg = async () => {
    if (!id) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/organizations/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(await parseApiError(res, 'Organisation konnte nicht gelöscht werden.'));
      }
      navigate(contactsHubPath('organizations'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Organisation konnte nicht gelöscht werden.');
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStartCreateLead = () => {
    const preferred = primaryContact ?? contacts[0] ?? null;
    setLeadCreatePrefillContactId(preferred ? String(preferred.id) : null);
    setLeadCreateOpen(true);
  };

  const handleSetPrimary = async (contactId: string | number) => {
    const res = await fetch(`/api/organizations/${id}/primary-contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: contactId }),
    });
    if (res.ok) void reload({ quiet: true });
  };

  const handleDocUpload = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/organizations/${id}/documents`, {
      method: 'POST',
      body: fd,
    });
    if (res.ok) void reload({ quiet: true });
  };

  const handleDocDelete = async (docId: string) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    const res = await fetch(`/api/organizations/${id}/documents/${docId}`, { method: 'DELETE' });
    if (res.ok) void reload({ quiet: true });
  };

  const handleSaveBilling = async (next: {
    billing_contact_id: string | null;
    billing_email_cc: string[];
    billing_address: string;
    billing_phone: string;
    billing_email: string;
  }) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/organizations/${id}/billing-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: next.billing_contact_id ?? null }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Rechnungskontakt konnte nicht gesetzt werden.');
      }

      if (next.billing_contact_id) {
        const contactRes = await fetch(`/api/contacts/${next.billing_contact_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address: next.billing_address.trim() || null,
            phone: next.billing_phone.trim() || null,
            email: next.billing_email.trim() || null,
          }),
        });
        if (!contactRes.ok) {
          await reload({ quiet: true });
          const body = (await contactRes.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Kontaktdaten konnten nicht gespeichert werden.');
        }
      }

      const patchRes = await fetch(`/api/organizations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billing_email_cc: next.billing_email_cc }),
      });
      if (!patchRes.ok) {
        await reload({ quiet: true });
        const body = (await patchRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'CC-Liste konnte nicht aktualisiert werden.');
      }

      await reload({ quiet: true });
      setEditingSection(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (patch: Partial<Organization>, options?: { confirmIbanChange?: boolean }) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/organizations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options?.confirmIbanChange ? { ...patch, confirm_iban_change: true } : patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string; previous_iban?: string };
        if (res.status === 409 && body.code === 'iban_change_confirmation_required') {
          setIbanConfirm({ patch, previous: String(body.previous_iban ?? '') });
          return;
        }
        throw new Error(body.error ?? 'Update fehlgeschlagen.');
      }
      const updated = (await res.json()) as Organization;
      setOrg(updated);
      setEditingSection(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
      <AppSidebar active="contacts" />

      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Organisation" />

        <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain bg-app-surface md:bg-transparent">
          <div className="max-w-[1180px] w-full mx-auto md:px-6 md:py-6">
          {error && (
            <div className="mx-4 md:mx-0 mt-3 md:mt-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3 md:mb-4">{error}</div>
          )}

          <div className="relative md:space-y-4 md:pl-11">
            <button
              type="button"
              onClick={goBack}
              className="hidden md:inline-flex absolute left-0 top-5 md:top-6 z-10 h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-gray-200/60 hover:text-app-text-muted"
              aria-label="Zurück"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="bg-app-surface border-b border-app-border/60 md:border md:border-app-border md:rounded-[10px] md:shadow-[0_1px_2px_rgba(0,0,0,0.03)] px-4 py-4 md:p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
              <span
                className={`w-12 h-12 md:w-16 md:h-16 rounded-full grid place-items-center text-lg md:text-xl font-bold shrink-0 ${organizationAvatarClassName(
                  org.organization_type,
                )}`}
              >
                {orgInitials(org.name)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg md:text-[22px] font-semibold text-app-text tracking-tight truncate">{org.name}</h1>
                  {org.organization_type && (
                    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                      <Building2 className="w-3 h-3 opacity-80" aria-hidden />
                      {org.organization_type}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-app-text-subtle">
                  {primaryContact
                    ? `${contactDisplayName(primaryContact)}${
                        primaryContact.contact_type ? ` · ${primaryContact.contact_type}` : ''
                      }`
                    : '—'}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-app-text-subtle min-w-0 w-full leading-snug">
                  <span className="shrink-0 tabular-nums">{org.customer_number ?? '—'}</span>
                  {lastProjectDateDe && (
                    <>
                      <span className="shrink-0">·</span>
                      <span>
                        Letztes Projekt am <span className="tabular-nums">{lastProjectDateDe}</span>
                      </span>
                    </>
                  )}
                  {org.customer_since && (
                    <>
                      <span className="shrink-0">·</span>
                      <span>Kunde seit {formatDateDe(org.customer_since)}</span>
                    </>
                  )}
                  {(primaryContact?.email || primaryContact?.phone) && (
                    <>
                      <span className="shrink-0 hidden sm:inline" aria-hidden>
                        ·
                      </span>
                      <span className="inline-flex items-center gap-0.5 shrink-0 ml-auto sm:ml-0">
                        {primaryContact?.email && (
                          <button
                            type="button"
                            onClick={openPrimaryContactEmailCompose}
                            disabled={emailComposeBusy}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-blue-600 disabled:opacity-50"
                            title="E-Mail"
                          >
                            {emailComposeBusy ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <Mail className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        {primaryContact?.phone && (
                          <a
                            href={`tel:${primaryContact.phone}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-blue-600"
                            title="Anrufen"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              </div>
              {projectsEnabled && (
              <div className="shrink-0 flex flex-row sm:flex-col items-baseline sm:items-end justify-between sm:justify-start gap-2 sm:gap-0 text-left sm:text-right border-t border-app-border/60 pt-3 sm:border-t-0 sm:pt-0">
                <span className={FILTER_GROUP_LABEL_CLASS}>Gesamtumsatz</span>
                {orgRevenueTotals.grossSum != null && orgRevenueTotals.netSum != null ? (
                  <div className="flex flex-col items-end">
                    <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                      {formatCurrency(orgRevenueTotals.grossSum)}
                    </span>
                    <span className="text-sm md:text-sm tabular-nums text-app-text-subtle leading-snug">
                      {formatCurrency(orgRevenueTotals.netSum)}
                    </span>
                  </div>
                ) : orgRevenueTotals.grossSum != null ? (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                    {formatCurrency(orgRevenueTotals.grossSum)}
                  </span>
                ) : orgRevenueTotals.netSum != null ? (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                    {formatCurrency(orgRevenueTotals.netSum)}
                  </span>
                ) : (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-gray-300 tracking-tight leading-tight">—</span>
                )}
              </div>
              )}
            </div>
            <CustomerPartySummary party={org} className="mt-3" />

          {/* 2-column layout — continuous list on mobile, cards on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-3 md:gap-4">
            {/* Left column: Ansprechpartner, Projekte, Materials, Documents */}
            <div className="order-2 lg:order-1 lg:col-span-2 md:space-y-4">
              <SectionCard
                title="Ansprechpartner"
                subtitle={`${contacts.length} ${contacts.length === 1 ? 'Kontakt' : 'Kontakte'}`}
                action={
                <button
                  type="button"
                  onClick={() => setContactModalOpen(true)}
                  className="shrink-0 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                >
                  <Plus className="w-3.5 h-3.5" /> Kontakt
                </button>
                }
              >
                {contacts.length === 0 ? (
                  <p className="text-sm text-app-text-subtle">Noch keine Ansprechpartner.</p>
                ) : (
                  // Schlichte Liste über die volle Kartenbreite: Name | E-Mail | Telefon (mobil untereinander).
                  <ul className="-mx-4 -my-3 md:-my-4 divide-y divide-app-border/60 list-none p-0">
                    {contacts.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center gap-3 min-w-0 px-4 py-2.5 cursor-pointer hover:bg-app-hover active:bg-app-surface-muted/80 transition-colors"
                        onClick={() => navigate(contactDetailPath(c.id))}
                      >
                        <span className="w-8 h-8 rounded-full bg-app-surface-muted text-app-text-muted inline-flex items-center justify-center text-xs font-semibold shrink-0">
                          {(contactDisplayName(c) || '?').slice(0, 1).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1 md:grid md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)] md:items-center md:gap-4">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-medium text-app-text truncate">
                              {contactDisplayName(c) || '—'}
                            </span>
                            {c.is_primary && (
                              <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                                Haupt
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-app-text-subtle truncate">{c.email?.trim() || '—'}</span>
                          <span className="block text-xs text-app-text-subtle truncate tabular-nums">
                            {c.phone?.trim() || '—'}
                          </span>
                        </div>
                        <div className="w-8 shrink-0 flex justify-end self-center">
                          {!c.is_primary && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleSetPrimary(c.id);
                              }}
                              className="p-1.5 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                              aria-label="Als Hauptansprechpartner setzen"
                              title="Als Hauptansprechpartner setzen"
                            >
                              <Star className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              {projectsEnabled && (
                <LeadProjectsSection
                  leads={leads}
                  headerAction={canOpen(projectsListPath()) && (
                    <button
                      type="button"
                      onClick={handleStartCreateLead}
                      className="shrink-0 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                    >
                      <Plus className="w-3.5 h-3.5" /> Projekt
                    </button>
                  )}
                />
              )}

              {materialsEnabled && (
                <SectionCard
                  title="Materialien"
                  subtitle={materials.length > 0 ? `${materials.length} Positionen` : ''}
                >
                  {materials.length === 0 ? (
                    <p className="text-sm text-app-text-subtle">Noch keine Materialdaten.</p>
                  ) : (
                    <ul className="divide-y divide-app-border/60">
                      {materials.slice(0, 12).map((m, idx) => (
                        <li key={`${m.material_id ?? m.item_name}-${idx}`} className="py-2 flex items-center gap-3">
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-app-text truncate">{m.item_name}</span>
                            <span className="block text-xs text-app-text-subtle tabular-nums">
                              {m.total_quantity.toFixed(2)} {m.unit ?? ''}
                            </span>
                          </span>
                          <span className="text-xs tabular-nums text-app-text-muted">{formatCurrency(m.total_value)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              )}

              {invoicesEnabled && (
                <SectionCard
                  title="Angebote &amp; Rechnungen"
                  subtitle={invoices.length > 0 ? `${invoices.length} Dokumente` : ''}
                >
                  {invoices.length === 0 ? (
                    <p className="text-sm text-app-text-subtle">Noch keine Angebote oder Rechnungen.</p>
                  ) : (
                    <InvoiceDocumentsList
                      invoices={invoices}
                      recipientEmail={org.billing_contact?.email}
                      recipientCc={(org.billing_email_cc ?? []).filter(Boolean).join(', ')}
                    />
                  )}
                </SectionCard>
              )}

              <SectionCard
                title="Dokumente"
                subtitle={documents.length > 0 ? `${documents.length} Dateien` : ''}
                action={
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleDocUpload(f);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="shrink-0 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                    >
                      <Upload className="w-3.5 h-3.5" /> Hochladen
                    </button>
                  </>
                }
              >
                {documents.length === 0 ? (
                  <p className="text-sm text-app-text-subtle">Noch keine Dokumente hochgeladen.</p>
                ) : (
                  <ul className="divide-y divide-app-border/60">
                    {documents.map((d) => (
                      <li key={d.id} className="py-2 flex items-center gap-3">
                        <FileText className="w-4 h-4 text-app-text-subtle shrink-0" />
                        <a
                          href={`/api/organizations/${id}/documents/${d.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 min-w-0 text-sm text-app-text hover:underline truncate"
                        >
                          {d.filename}
                        </a>
                        <span className="text-xs text-app-text-subtle tabular-nums shrink-0">{formatDateDe(d.created_at)}</span>
                        <button
                          type="button"
                          onClick={() => void handleDocDelete(d.id)}
                          className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            {/* Right column: Allgemein, Rechnungskontakt, Rahmenvertrag, Stammdaten */}
            <div className="order-1 lg:order-2 md:space-y-4">
              <SectionCard
                title="Allgemein"
                action={
                  <OrgSectionEditToggle
                    section="allgemein"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Allgemein"
                  />
                }
              >
                {editingSection === 'allgemein' ? (
                  <EditOrgAllgemeinForm
                    org={org}
                    saving={saving}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : (
                  <>
                    <DataRow label="Name" value={org.name || '—'} />
                    <DataRow label="Typ" value={org.organization_type || '—'} />
                    <DataRow label="Firmensitz" value={org.registered_address?.trim() || '—'} />
                    <DataRow label="USt-IdNr." value={org.vat_id?.trim() || '—'} />
                    <DataRow label="Steuernummer" value={org.tax_number?.trim() || '—'} />
                    <DataRow label="Handelsregisternr." value={org.commercial_register_number?.trim() || '—'} />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Rechnungskontakt"
                action={
                  <OrgSectionEditToggle
                    section="billing"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Rechnungskontakt"
                  />
                }
              >
                {editingSection === 'billing' ? (
                  <EditOrgBillingForm
                    org={org}
                    contacts={contacts}
                    saving={saving}
                    onSave={handleSaveBilling}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : (
                  <>
                    <DataRow
                      label="Kontakt"
                      value={org.billing_contact ? contactDisplayName(org.billing_contact) || '—' : '—'}
                    />
                    <DataRow label="Adresse" value={org.billing_contact?.address || '—'} />
                    <DataRow label="Telefon" value={org.billing_contact?.phone || '—'} />
                    <DataRow label="E-Mail" value={org.billing_contact?.email || '—'} />
                    <DataRow
                      label="CC"
                      value={
                        (org.billing_email_cc ?? []).length === 0
                          ? '—'
                          : (org.billing_email_cc ?? []).join(', ')
                      }
                    />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Rahmenvertrag"
                action={
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        org.has_framework_contract
                          ? 'bg-green-100 text-green-700'
                          : 'bg-app-surface-muted text-app-text-muted'
                      }`}
                    >
                      {org.has_framework_contract ? 'Aktiv' : 'Kein Vertrag'}
                    </span>
                    <OrgSectionEditToggle
                      section="framework"
                      current={editingSection}
                      onChange={setEditingSection}
                      label="Rahmenvertrag"
                    />
                  </div>
                }
              >
                {editingSection === 'framework' ? (
                  <EditOrgFrameworkForm
                    org={org}
                    saving={saving}
                    showConstructionDeductions={showConstructionDeductions}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : (
                  <>
                    <DataRow label="Rabatt" value={formatPercent(org.discount_percent)} />
                    <DataRow
                      label="Zahlungsziel"
                      value={org.payment_terms_days != null ? `${org.payment_terms_days} Tage` : '—'}
                    />
                    <DataRow label="Kontoinhaber" value={org.bank_account_holder || '—'} />
                    <DataRow label="IBAN" value={org.iban ? formatIbanDisplay(org.iban) : '—'} />
                    <DataRow label="BIC" value={org.bic || '—'} />
                    <DataRow
                      label="Skonto"
                      value={
                        org.skonto_percent != null
                          ? `${formatPercent(org.skonto_percent)}${
                              org.skonto_days != null ? ` / ${org.skonto_days} T.` : ''
                            }`
                          : '—'
                      }
                    />
                    {showConstructionDeductions ? (
                      <>
                        <DataRow
                          label="Baunebenkosten"
                          value={
                            org.site_levy_percent != null ? formatPercent(org.site_levy_percent) : '—'
                          }
                        />
                        <DataRow
                          label="Einbehalt Abschlag"
                          value={
                            org.retention_percent_partial != null
                              ? formatPercent(org.retention_percent_partial)
                              : '—'
                          }
                        />
                        <DataRow
                          label="Einbehalt Schluss"
                          value={
                            org.retention_percent_final != null
                              ? formatPercent(org.retention_percent_final)
                              : '—'
                          }
                        />
                      </>
                    ) : null}
                    <DataRow
                      label="Stundensatz"
                      value={org.hourly_rate != null ? formatCurrency(org.hourly_rate) : '—'}
                    />
                    <DataRow
                      label="Laufzeit"
                      value={org.framework_valid_until ? `bis ${formatDateDe(org.framework_valid_until)}` : '—'}
                    />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Stammdaten"
                action={
                  <OrgSectionEditToggle
                    section="stammdaten"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Stammdaten"
                  />
                }
              >
                {editingSection === 'stammdaten' ? (
                  <EditOrgStammdatenForm
                    org={org}
                    saving={saving}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : (
                  <>
                    <DataRow label="Kundennr." value={org.customer_number ?? '—'} />
                    {org.organization_type === 'Lieferant' ? (
                      <DataRow label="Unsere Kundennr." value={org.supplier_customer_number?.trim() || '—'} />
                    ) : null}
                    <DataRow label="Herkunft" value={org.origin || '—'} />
                    <DataRow label="Kostenstelle" value={org.cost_center || '—'} />
                    <DataRow label="Erstellt" value={formatDateDe(org.created_at)} />
                  </>
                )}
              </SectionCard>

              {datev.enabled ? (
                <SectionCard
                  title="DATEV"
                  action={
                    <OrgSectionEditToggle section="datev" current={editingSection} onChange={setEditingSection} label="DATEV" />
                  }
                >
                  {editingSection === 'datev' ? (
                    <EditOrgDatevForm
                      org={org}
                      costCenters={datevCostCenters}
                      saving={saving}
                      onSave={handleSave}
                      onCancel={() => setEditingSection(null)}
                    />
                  ) : (
                    <>
                      <DataRow label="Debitor" value={org.datev_debitor_number ? String(org.datev_debitor_number) : 'Beim ersten Export'} />
                      {org.organization_type === 'Lieferant' || org.datev_kreditor_number ? (
                        <DataRow label="Kreditor" value={org.datev_kreditor_number ? String(org.datev_kreditor_number) : 'Beim ersten Export'} />
                      ) : null}
                      {org.organization_type === 'Lieferant' ? (
                        <DataRow label="Aufwandskonto" value={org.default_expense_account || 'Standard'} />
                      ) : null}
                      <DataRow
                        label="Kostenstelle"
                        value={datevCostCenters.find((c) => c.id === org.default_cost_center_id)?.code ?? '—'}
                      />
                    </>
                  )}
                </SectionCard>
              ) : null}

              <SectionCard
                title="Notizen"
                action={
                  <OrgSectionEditToggle
                    section="notes"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Notizen"
                  />
                }
              >
                {editingSection === 'notes' ? (
                  <EditOrgNotizenForm
                    org={org}
                    saving={saving}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : org.notes?.trim() ? (
                  <p className="text-xs text-app-text-muted whitespace-pre-wrap">{org.notes}</p>
                ) : (
                  <p className="text-sm text-app-text-subtle">Keine Notizen.</p>
                )}
              </SectionCard>

              <div className="px-4 py-4 md:px-0 md:py-0">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleting}
                  className="app-btn-danger-outline inline-flex h-9 items-center gap-1.5 px-3.5 border border-red-200 rounded-md bg-app-surface text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                  aria-label="Organisation löschen"
                >
                  <Trash2 className="w-3.5 h-3.5 shrink-0" />
                  Organisation löschen
                </button>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>
      </main>

      {contactModalOpen && (
        <CreateContactModal
          variant="drawer"
          onClose={() => setContactModalOpen(false)}
          defaultOrganizationId={org.id}
          defaultKind="Gewerblicher Kontakt"
          onCreated={() => {
            setContactModalOpen(false);
            void reload({ quiet: true });
          }}
        />
      )}

      {leadCreateOpen && (
        <CreateLeadModal
          onClose={() => {
            setLeadCreateOpen(false);
            setLeadCreatePrefillContactId(null);
          }}
          onCreated={(lead: Lead) => {
            navigate(leadDetailPath(lead));
          }}
          prefillOrganizationId={org.id}
          prefillContactId={leadCreatePrefillContactId}
        />
      )}
      {emailCompose && (
        <ComposeEmailModal
          key={`organization-mail-${emailComposeResetKey}`}
          open
          accountId={emailCompose.accountId}
          resetKey={emailComposeResetKey}
          initialTo={emailCompose.to}
          initialCc={emailCompose.cc}
          initialSubject={emailCompose.subject}
          initialBody={emailCompose.body}
          initialFromEmail={emailCompose.fromEmail}
          overlayClassName="z-[260]"
          onClose={() => setEmailCompose(null)}
          onSent={() => setEmailCompose(null)}
        />
      )}

      <AppConfirmDialog
        open={ibanConfirm != null}
        onClose={() => !saving && setIbanConfirm(null)}
        title="Bankverbindung ändern?"
        description={
          ibanConfirm ? (
            <>
              Die IBAN ändert sich von{' '}
              <span className="font-mono font-semibold text-app-text">{formatIbanDisplay(ibanConfirm.previous)}</span> auf{' '}
              <span className="font-mono font-semibold text-app-text">
                {ibanConfirm.patch.iban ? formatIbanDisplay(String(ibanConfirm.patch.iban)) : '(leer)'}
              </span>
              . Geänderte Bankdaten sind ein häufiger Betrugsversuch. Bitte vorher telefonisch beim Lieferanten (bekannte Nummer, nicht aus der E-Mail) bestätigen lassen. Die Änderung wird protokolliert.
            </>
          ) : null
        }
        confirmLabel="Geprüft, IBAN ändern"
        cancelLabel="Abbrechen"
        tone="warning"
        isLoading={saving}
        onConfirm={() => {
          const pending = ibanConfirm;
          setIbanConfirm(null);
          if (pending) void handleSave(pending.patch, { confirmIbanChange: true });
        }}
      />

      <AppConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        title="Organisation löschen?"
        description={
          org ? (
            <>
              Soll die Organisation{' '}
              <span className="font-semibold text-app-text">„{org.name}“</span> wirklich gelöscht werden? Sie
              erscheint danach nicht mehr in der Übersicht.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleting}
        onConfirm={executeDeleteOrg}
      />
    </div>
  );
};

/* ----------------------------- Helpers / UI ------------------------------- */

const EditOrgDatevForm: React.FC<{
  org: Organization;
  costCenters: DatevCostCenter[];
  saving: boolean;
  onSave: (patch: Partial<Organization>) => Promise<void> | void;
  onCancel: () => void;
}> = ({ org, costCenters, saving, onSave, onCancel }) => {
  const [account, setAccount] = React.useState(org.default_expense_account ?? '');
  const [costCenterId, setCostCenterId] = React.useState(org.default_cost_center_id ?? '');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave({ default_expense_account: account.trim() || null, default_cost_center_id: costCenterId || null });
      }}
    >
      {org.organization_type === 'Lieferant' ? (
        <EditDataRow label="Aufwandskonto">
          <input
            inputMode="numeric"
            value={account}
            placeholder="Standard aus den DATEV-Einstellungen"
            onChange={(e) => setAccount(e.target.value.replace(/[^\d]/g, '').slice(0, 8))}
            className={APP_INPUT_CLASS}
          />
        </EditDataRow>
      ) : null}
      <EditDataRow label="Kostenstelle">
        <AppSelect
          value={costCenterId}
          onChange={setCostCenterId}
          options={[
            { value: '', label: 'Keine' },
            ...costCenters
              .filter((c) => c.active || c.id === costCenterId)
              .map((c) => ({ value: c.id, label: c.name ? `${c.code} · ${c.name}` : c.code })),
          ]}
          aria-label="Standard-Kostenstelle"
        />
      </EditDataRow>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className={APP_BTN_OUTLINE_CLASS} disabled={saving} onClick={onCancel}>Abbrechen</button>
        <button type="submit" className={APP_BTN_PRIMARY_CLASS} disabled={saving}>Speichern</button>
      </div>
    </form>
  );
};

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <section className="bg-app-surface border-b border-app-border/60 md:border md:border-app-border md:rounded-lg md:shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:overflow-hidden last:border-b-0 md:last:border">
    <header className="px-4 py-3 min-h-11 border-b border-app-border/60 flex items-center justify-between gap-2">
      <div className="flex items-baseline gap-2 min-w-0">
        <h2 className="text-sm font-semibold text-app-text shrink-0">{title}</h2>
        {subtitle && <span className="text-sm text-app-text-subtle truncate">{subtitle}</span>}
      </div>
      <div className="shrink-0 flex items-center gap-1">{action}</div>
    </header>
    <div className="px-4 py-3 md:p-4">{children}</div>
  </section>
);

type OrgEditSection = 'allgemein' | 'billing' | 'framework' | 'stammdaten' | 'notes' | 'datev';

const OrgSectionEditToggle: React.FC<{
  section: OrgEditSection;
  current: OrgEditSection | null;
  onChange: (v: OrgEditSection | null) => void;
  label: string;
}> = ({ section, current, onChange, label }) =>
  current === section ? (
    <button
      type="button"
      onClick={() => onChange(null)}
      className="shrink-0 inline-flex items-center justify-center rounded text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
      aria-label="Bearbeitung abbrechen"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => onChange(section)}
      disabled={current !== null}
      className="shrink-0 inline-flex items-center justify-center rounded text-gray-400 hover:text-app-text-muted hover:bg-app-hover disabled:opacity-30 disabled:pointer-events-none"
      aria-label={`${label} bearbeiten`}
    >
      <Edit2 className="w-3.5 h-3.5" />
    </button>
  );

const DataRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[110px_1fr] sm:gap-3 py-2.5 sm:py-2 sm:items-baseline">
    <span className={FILTER_GROUP_LABEL_CLASS}>{label}</span>
    <span className="text-sm text-app-text leading-snug break-words">{value || '—'}</span>
  </div>
);

/** Label above field on mobile; side-by-side from `sm`. */
const EditDataRow: React.FC<{ label: string; alignTop?: boolean; children: React.ReactNode }> = ({
  label,
  alignTop,
  children,
}) => (
  <div
    className={`grid grid-cols-1 gap-1 sm:grid-cols-[110px_1fr] sm:gap-3 py-2.5 sm:py-2 ${
      alignTop ? 'sm:items-start' : 'sm:items-center'
    }`}
  >
    <span
      className={`${FILTER_GROUP_LABEL_CLASS} shrink-0 ${
        alignTop ? 'sm:pt-1.5' : ''
      }`}
    >
      {label}
    </span>
    <div className="min-w-0">{children}</div>
  </div>
);

const orgFormFooter = (onCancel: () => void, saving: boolean) => (
  <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-app-border/60">
    <button type="button" onClick={onCancel} className={APP_BTN_OUTLINE_CLASS}>
      Abbrechen
    </button>
    <button type="submit" disabled={saving} className={APP_BTN_PRIMARY_CLASS}>
      {saving ? 'Speichern …' : 'Speichern'}
    </button>
  </div>
);

const orgNumericInput = (v: number | null): string => (v == null ? '' : String(v));
const orgNumOrNull = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Erlaubt Eingaben wie `42`, `42,5`, `42,50` (max. 2 Nachkommastellen). */
const isValidOrgDecimal2Input = (raw: string): boolean =>
  raw === '' || /^\d+([.,]\d{0,2})?$/.test(raw);

const orgDecimal2InputFromNumber = (v: number | null): string => {
  if (v == null) return '';
  return String(v).replace('.', ',');
};

const orgDecimal2FromInput = (raw: string): number | null => {
  let t = raw.trim();
  if (!t) return null;
  if (t.endsWith(',') || t.endsWith('.')) t = t.slice(0, -1);
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};
const orgIntOrNull = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
};

const EditOrgAllgemeinForm: React.FC<{
  org: Organization;
  saving: boolean;
  onSave: (patch: Partial<Organization>) => void | Promise<void>;
  onCancel: () => void;
}> = ({ org, saving, onSave, onCancel }) => {
  const [form, setForm] = React.useState({
    name: org.name,
    organization_type: org.organization_type,
    registered_address: org.registered_address ?? '',
    vat_id: org.vat_id ?? '',
    tax_number: org.tax_number ?? '',
    commercial_register_number: org.commercial_register_number ?? '',
  });
  React.useEffect(
    () =>
      setForm({
        name: org.name,
        organization_type: org.organization_type,
        registered_address: org.registered_address ?? '',
        vat_id: org.vat_id ?? '',
        tax_number: org.tax_number ?? '',
        commercial_register_number: org.commercial_register_number ?? '',
      }),
    [org],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSave({
      name: form.name,
      organization_type: form.organization_type,
      registered_address: form.registered_address.trim(),
      vat_id: form.vat_id.trim(),
      tax_number: form.tax_number.trim(),
      commercial_register_number: form.commercial_register_number.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Name">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Typ">
        <OrganizationTypePicker
          value={form.organization_type}
          onChange={(t) => setForm((prev) => ({ ...prev, organization_type: t }))}
          variant="detail"
        />
      </EditDataRow>
      <EditDataRow label="Firmensitz" alignTop>
        <AddressAutocompleteInput
          value={form.registered_address}
          onChange={(v) => setForm((prev) => ({ ...prev, registered_address: v }))}
          inputClassName="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
          placeholder="Straße, Hausnummer, PLZ Ort"
        />
      </EditDataRow>
      <EditDataRow label="USt-IdNr.">
        <input
          type="text"
          value={form.vat_id}
          onChange={(e) => setForm((prev) => ({ ...prev, vat_id: e.target.value }))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
          placeholder="DE123456789"
          autoComplete="off"
        />
      </EditDataRow>
      <EditDataRow label="Steuernummer">
        <input
          type="text"
          value={form.tax_number}
          onChange={(e) => setForm((prev) => ({ ...prev, tax_number: e.target.value }))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
          placeholder="z. B. 12/123/12345"
          autoComplete="off"
        />
      </EditDataRow>
      <EditDataRow label="Handelsregisternr.">
        <input
          type="text"
          value={form.commercial_register_number}
          onChange={(e) => setForm((prev) => ({ ...prev, commercial_register_number: e.target.value }))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
          placeholder="z. B. HRB 12345"
          autoComplete="off"
        />
      </EditDataRow>
      {orgFormFooter(onCancel, saving)}
    </form>
  );
};

/** App-styled picker (no native `<select>`) for billing contact inside org billing form. */
const BillingContactPicker: React.FC<{
  contacts: Contact[];
  value: string | null;
  onChange: (id: string | null) => void;
}> = ({ contacts, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [menuPos, setMenuPos] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const selected = contacts.find((c) => String(c.id) === String(value)) ?? null;
  const display =
    value == null
      ? '— Kein Rechnungskontakt —'
      : `${contactDisplayName(selected) || selected?.email || `Kontakt #${value}`}${
          selected?.email ? ` · ${selected.email}` : ''
        }`.trim() || `Kontakt #${value}`;

  const updateMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const maxPanel = 280;
    let left = rect.left;
    const width = rect.width;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    if (left < margin) left = margin;
    setMenuPos({
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: Math.min(maxPanel, Math.max(0, spaceBelow)),
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (listRef.current && (listRef.current === t || listRef.current.contains(t))) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      setSearch('');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        contactDisplayName(c).toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        String(c.id).includes(q),
    );
  }, [contacts, q]);

  return (
    <div className="relative min-w-0" ref={wrapRef}>
      <div ref={triggerRef}>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => {
            setOpen((o) => !o);
            if (open) setSearch('');
          }}
          className="w-full flex items-center justify-between gap-2 px-2 py-2 text-left text-sm font-medium bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none"
        >
          <span className={`truncate ${value == null ? 'text-app-text-subtle' : 'text-app-text'}`}>{display}</span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 300,
            }}
            className="bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col min-h-0 overscroll-y-contain"
            onWheel={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-app-border shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suchen…"
                  className="w-full pl-9 pr-2 py-2 text-sm border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
                setSearch('');
              }}
              className={`w-full shrink-0 text-left px-2.5 py-2.5 text-sm font-medium border-b border-app-border transition-colors ${
                value == null ? 'bg-app-surface-muted text-app-text' : 'text-app-text-muted hover:bg-app-hover'
              }`}
            >
              — Kein Rechnungskontakt —
            </button>
            <div ref={listRef} className="overflow-y-auto flex-1 min-h-0 touch-pan-y max-h-[220px]">
              {filtered.length === 0 ? (
                <p className="px-2.5 py-2.5 text-sm text-app-text-subtle">Keine Treffer.</p>
              ) : (
                filtered.map((c) => {
                  const active = String(c.id) === String(value);
                  return (
                    <button
                      key={String(c.id)}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(String(c.id));
                        setOpen(false);
                        setSearch('');
                      }}
                      className={`w-full text-left px-2.5 py-2.5 text-sm font-medium transition-colors ${
                        active ? 'bg-pipedrive-blue text-white' : 'text-app-text hover:bg-app-hover'
                      }`}
                    >
                      {contactDisplayName(c) || c.email || `Kontakt #${c.id}`}
                      {c.email ? ` · ${c.email}` : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const EditOrgBillingForm: React.FC<{
  org: Organization;
  contacts: Contact[];
  saving: boolean;
  onSave: (next: {
    billing_contact_id: string | null;
    billing_email_cc: string[];
    billing_address: string;
    billing_phone: string;
    billing_email: string;
  }) => void | Promise<void>;
  onCancel: () => void;
}> = ({ org, contacts, saving, onSave, onCancel }) => {
  const [billingContactId, setBillingContactId] = React.useState<string | null>(
    org.billing_contact_id ?? null,
  );
  const [ccText, setCcText] = React.useState((org.billing_email_cc ?? []).join(', '));
  const [address, setAddress] = React.useState(org.billing_contact?.address ?? '');
  const [phone, setPhone] = React.useState(org.billing_contact?.phone ?? '');
  const [email, setEmail] = React.useState(org.billing_contact?.email ?? '');
  const prevBillingContactIdRef = React.useRef(billingContactId);

  React.useEffect(() => {
    setBillingContactId(org.billing_contact_id ?? null);
  }, [org.billing_contact_id]);
  React.useEffect(() => {
    setCcText((org.billing_email_cc ?? []).join(', '));
  }, [org.billing_email_cc]);
  React.useEffect(() => {
    setAddress(org.billing_contact?.address ?? '');
    setPhone(org.billing_contact?.phone ?? '');
    setEmail(org.billing_contact?.email ?? '');
    prevBillingContactIdRef.current = org.billing_contact_id ?? null;
  }, [org.billing_contact_id, org.billing_contact?.address, org.billing_contact?.email, org.billing_contact?.phone]);

  const handleBillingContactChange = (nextId: string | null) => {
    setBillingContactId(nextId);
    if (prevBillingContactIdRef.current === nextId) return;
    prevBillingContactIdRef.current = nextId;
    const selected = nextId
      ? contacts.find((c) => String(c.id) === String(nextId)) ?? null
      : null;
    setAddress(selected?.address ?? '');
    setPhone(selected?.phone ?? '');
    setEmail(selected?.email ?? '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const ccs = ccText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    void onSave({
      billing_contact_id: billingContactId,
      billing_email_cc: ccs,
      billing_address: address,
      billing_phone: phone,
      billing_email: email,
    });
  };

  const contactFieldsDisabled = !billingContactId;

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Kontakt">
        {contacts.length === 0 ? (
          <p className="text-sm text-app-text-subtle">
            Noch keine Kontakte. Lege erst einen Ansprechpartner an.
          </p>
        ) : (
          <BillingContactPicker
            contacts={contacts}
            value={billingContactId}
            onChange={handleBillingContactChange}
          />
        )}
      </EditDataRow>
      <EditDataRow label="Adresse" alignTop>
        <AddressAutocompleteInput
          value={address}
          onChange={setAddress}
          disabled={contactFieldsDisabled}
          inputClassName="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm disabled:bg-app-surface-muted disabled:text-slate-400"
          placeholder={contactFieldsDisabled ? 'Zuerst Kontakt wählen' : 'Straße, Hausnummer, PLZ Ort'}
        />
      </EditDataRow>
      <EditDataRow label="Telefon">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={contactFieldsDisabled}
          placeholder={contactFieldsDisabled ? 'Zuerst Kontakt wählen' : undefined}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm disabled:bg-app-surface-muted disabled:text-slate-400"
        />
      </EditDataRow>
      <EditDataRow label="E-Mail">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={contactFieldsDisabled}
          placeholder={contactFieldsDisabled ? 'Zuerst Kontakt wählen' : undefined}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm disabled:bg-app-surface-muted disabled:text-slate-400"
        />
      </EditDataRow>
      <EditDataRow label="CC" alignTop>
        <textarea
          rows={2}
          value={ccText}
          onChange={(e) => setCcText(e.target.value)}
          placeholder="Zusätzliche Empfänger, mit Komma trennen"
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm resize-none"
        />
      </EditDataRow>
      {orgFormFooter(onCancel, saving)}
    </form>
  );
};

const EditOrgFrameworkForm: React.FC<{
  org: Organization;
  saving: boolean;
  showConstructionDeductions: boolean;
  onSave: (patch: Partial<Organization>) => void | Promise<void>;
  onCancel: () => void;
}> = ({ org, saving, showConstructionDeductions, onSave, onCancel }) => {
  const [form, setForm] = React.useState(org);
  const [hourlyRateText, setHourlyRateText] = React.useState(() =>
    orgDecimal2InputFromNumber(org.hourly_rate),
  );
  const [ibanError, setIbanError] = React.useState<string | null>(null);
  React.useEffect(() => {
    setForm(org);
    setHourlyRateText(orgDecimal2InputFromNumber(org.hourly_rate));
    setIbanError(null);
  }, [org]);

  const update = <K extends keyof Organization>(k: K, v: Organization[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const iban = normalizeIban(form.iban) || null;
    if (iban && !isValidIban(iban)) {
      setIbanError('IBAN ist ungültig.');
      return;
    }
    setIbanError(null);
    void onSave({
      has_framework_contract: form.has_framework_contract,
      discount_percent: form.discount_percent,
      payment_terms_days: form.payment_terms_days,
      iban,
      bic: form.bic ?? null,
      bank_account_holder: form.bank_account_holder ?? null,
      skonto_percent: form.skonto_percent,
      skonto_days: form.skonto_days,
      site_levy_percent: form.site_levy_percent ?? null,
      retention_percent_partial: form.retention_percent_partial ?? null,
      retention_percent_final: form.retention_percent_final ?? null,
      hourly_rate: orgDecimal2FromInput(hourlyRateText),
      framework_valid_until: form.framework_valid_until,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Aktiv">
        <input
          type="checkbox"
          checked={form.has_framework_contract}
          onChange={(e) => update('has_framework_contract', e.target.checked)}
        />
      </EditDataRow>
      <EditDataRow label="Rabatt %">
        <input
          type="text"
          inputMode="decimal"
          value={orgNumericInput(form.discount_percent)}
          onChange={(e) => update('discount_percent', orgNumOrNull(e.target.value))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Zahlungsziel (T.)">
        <input
          type="text"
          inputMode="numeric"
          value={orgNumericInput(form.payment_terms_days)}
          onChange={(e) => update('payment_terms_days', orgIntOrNull(e.target.value))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Kontoinhaber">
        <input
          type="text"
          value={form.bank_account_holder ?? ''}
          onChange={(e) => update('bank_account_holder', e.target.value)}
          className="w-full h-9 px-2 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      <EditDataRow label="IBAN">
        <div>
          <input
            type="text"
            value={form.iban ?? ''}
            onChange={(e) => {
              setIbanError(null);
              update('iban', e.target.value);
            }}
            className="w-full h-9 px-2 bg-app-surface border border-app-border rounded-md text-sm font-mono"
            autoComplete="off"
          />
          {ibanError ? <p className="mt-1 text-xs text-rose-600">{ibanError}</p> : null}
        </div>
      </EditDataRow>
      <EditDataRow label="BIC">
        <input
          type="text"
          value={form.bic ?? ''}
          onChange={(e) => update('bic', e.target.value)}
          className="w-full h-9 px-2 bg-app-surface border border-app-border rounded-md text-sm font-mono uppercase"
          autoComplete="off"
        />
      </EditDataRow>
      <EditDataRow label="Skonto %">
        <input
          type="text"
          inputMode="decimal"
          value={orgNumericInput(form.skonto_percent)}
          onChange={(e) => update('skonto_percent', orgNumOrNull(e.target.value))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Skonto (T.)">
        <input
          type="text"
          inputMode="numeric"
          value={orgNumericInput(form.skonto_days)}
          onChange={(e) => update('skonto_days', orgIntOrNull(e.target.value))}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      {showConstructionDeductions ? (
        <>
          <EditDataRow label="Baunebenkosten %">
            <input
              type="text"
              inputMode="decimal"
              value={orgNumericInput(form.site_levy_percent ?? null)}
              onChange={(e) => update('site_levy_percent', orgNumOrNull(e.target.value))}
              className="h-9 w-full rounded-md border border-app-border bg-app-surface px-2 text-sm"
              placeholder="z. B. 4"
            />
          </EditDataRow>
          <EditDataRow label="Einbehalt Abschlag %">
            <input
              type="text"
              inputMode="decimal"
              value={orgNumericInput(form.retention_percent_partial ?? null)}
              onChange={(e) => update('retention_percent_partial', orgNumOrNull(e.target.value))}
              className="h-9 w-full rounded-md border border-app-border bg-app-surface px-2 text-sm"
            />
          </EditDataRow>
          <EditDataRow label="Einbehalt Schluss %">
            <input
              type="text"
              inputMode="decimal"
              value={orgNumericInput(form.retention_percent_final ?? null)}
              onChange={(e) => update('retention_percent_final', orgNumOrNull(e.target.value))}
              className="h-9 w-full rounded-md border border-app-border bg-app-surface px-2 text-sm"
            />
          </EditDataRow>
        </>
      ) : null}
      <EditDataRow label="Stundensatz €">
        <input
          type="text"
          inputMode="decimal"
          value={hourlyRateText}
          onChange={(e) => {
            const raw = e.target.value;
            if (!isValidOrgDecimal2Input(raw)) return;
            setHourlyRateText(raw);
            update('hourly_rate', orgDecimal2FromInput(raw));
          }}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Laufzeit bis">
        <input
          type="date"
          value={form.framework_valid_until ?? ''}
          onChange={(e) => update('framework_valid_until', e.target.value || null)}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      {orgFormFooter(onCancel, saving)}
    </form>
  );
};

const EditOrgStammdatenForm: React.FC<{
  org: Organization;
  saving: boolean;
  onSave: (patch: Partial<Organization>) => void | Promise<void>;
  onCancel: () => void;
}> = ({ org, saving, onSave, onCancel }) => {
  const [form, setForm] = React.useState(org);
  React.useEffect(() => setForm(org), [org]);

  const update = <K extends keyof Organization>(k: K, v: Organization[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };
  // Eigene Kundennummer beim Lieferanten gibt es nur bei Lieferanten.
  const isSupplier = org.organization_type === 'Lieferant';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Partial<Organization> = {
      origin: form.origin,
      cost_center: form.cost_center,
    };
    if (isSupplier) {
      patch.supplier_customer_number = form.supplier_customer_number?.trim() || null;
    }
    if (org.numbering_manual_edit_enabled !== false) {
      patch.customer_number = form.customer_number?.trim() || undefined;
    }
    void onSave(patch);
  };

  const canEditCustomerNumber = org.numbering_manual_edit_enabled !== false;

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Kundennr.">
        {canEditCustomerNumber ? (
          <input
            type="text"
            value={form.customer_number ?? ''}
            onChange={(e) => update('customer_number', e.target.value)}
            className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm font-mono tabular-nums"
            spellCheck={false}
            autoComplete="off"
          />
        ) : (
          <span className="text-sm font-mono tabular-nums text-app-text">
            {form.customer_number ?? '—'}
          </span>
        )}
      </EditDataRow>
      {isSupplier ? (
        <EditDataRow label="Unsere Kundennr.">
          <input
            type="text"
            value={form.supplier_customer_number ?? ''}
            onChange={(e) => update('supplier_customer_number', e.target.value)}
            placeholder="Kundennummer beim Lieferanten"
            className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm font-mono tabular-nums"
            spellCheck={false}
            autoComplete="off"
          />
        </EditDataRow>
      ) : null}
      <EditDataRow label="Herkunft">
        <input
          type="text"
          value={form.origin}
          onChange={(e) => update('origin', e.target.value)}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Kostenstelle">
        <input
          type="text"
          value={form.cost_center}
          onChange={(e) => update('cost_center', e.target.value)}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm"
        />
      </EditDataRow>
      {orgFormFooter(onCancel, saving)}
    </form>
  );
};

const EditOrgNotizenForm: React.FC<{
  org: Organization;
  saving: boolean;
  onSave: (patch: Partial<Organization>) => void | Promise<void>;
  onCancel: () => void;
}> = ({ org, saving, onSave, onCancel }) => {
  const [notes, setNotes] = React.useState(org.notes ?? '');
  React.useEffect(() => setNotes(org.notes ?? ''), [org.notes]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSave({ notes: notes.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Notizen" alignTop>
        <textarea
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-2 py-1.5 bg-app-surface border border-app-border rounded text-sm resize-y min-h-[5rem]"
        />
      </EditDataRow>
      {orgFormFooter(onCancel, saving)}
    </form>
  );
};
